import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { budgetsAPI, categoriesAPI } from '../api';
import useAuthStore from '../store/auth';
import { formatAmount } from '../utils/currency';

export default function BudgetsPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const currency = user?.currency || 'EUR';
  const fmt = n => formatAmount(n, currency);
  const qc = useQueryClient();
  const [month, setMonth] = React.useState(new Date().toISOString().slice(0, 7));
  const [form, setForm]   = React.useState({ category_id:'', amount:'' });
  const [showModal, setShowModal] = React.useState(false);

  const { data: budgets, isLoading } = useQuery({
    queryKey: ['budgets', month],
    queryFn: () => budgetsAPI.list({ month }).then(r => r.data),
  });
  const { data: cats } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesAPI.list().then(r => r.data),
  });

  const saveMut = useMutation({
    mutationFn: d => budgetsAPI.create({ ...d, month }),
    onSuccess: () => { qc.invalidateQueries(['budgets']); setShowModal(false); toast.success(t('budget_saved')); },
    onError: () => toast.error(t('error')),
  });
  const deleteMut = useMutation({
    mutationFn: id => budgetsAPI.remove(id),
    onSuccess: () => { qc.invalidateQueries(['budgets']); toast.success(t('budget_deleted')); },
  });

  const inp = { width:'100%', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', color:'var(--text)', fontSize:14, outline:'none', boxSizing:'border-box' };

  const totalBudget = (budgets || []).reduce((s, b) => s + parseFloat(b.amount || 0), 0);
  const totalSpent  = (budgets || []).reduce((s, b) => s + parseFloat(b.spent  || 0), 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontFamily:'Syne,sans-serif', fontSize:24, fontWeight:700 }}>{t('budgets')}</h1>
          <p style={{ color:'var(--text2)', fontSize:14, marginTop:4 }}>
            {fmt(totalSpent)} / {fmt(totalBudget)} {t('spent').toLowerCase()}
          </p>
        </div>
        <button onClick={() => { setForm({ category_id: cats?.[0]?.id || '', amount:'' }); setShowModal(true); }}
          style={{ padding:'10px 20px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, cursor:'pointer' }}>
          + {t('add_budget')}
        </button>
      </div>

      <div style={{ marginBottom:20 }}>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', color:'var(--text)', fontSize:14, outline:'none' }} />
      </div>

      {isLoading ? <p style={{ color:'var(--text2)', padding:40, textAlign:'center' }}>{t('loading')}</p>
      : (budgets || []).length === 0 ? (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:60, textAlign:'center', color:'var(--text3)' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🎯</div>
          <p>{t('no_budgets')}</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
          {(budgets || []).map(b => {
            const pct  = b.amount > 0 ? Math.min((b.spent / b.amount) * 100, 100) : 0;
            const over = parseFloat(b.spent) > parseFloat(b.amount);
            const color = over ? 'var(--danger)' : pct > 80 ? '#f7c26a' : 'var(--accent3)';
            return (
              <div key={b.id} style={{ background:'var(--surface)', border:'1px solid ' + (over ? 'var(--danger)' : 'var(--border)'), borderRadius:16, padding:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:22 }}>{b.category_icon}</span>
                    <div>
                      <div style={{ fontWeight:600, fontSize:15 }}>{b.category_name}</div>
                      <div style={{ fontSize:12, color:'var(--text2)' }}>{t('monthly_budget')}</div>
                    </div>
                  </div>
                  <button onClick={() => { if (window.confirm(t('confirm_delete'))) deleteMut.mutate(b.id); }}
                    style={{ background:'none', border:'none', color:'var(--text3)', fontSize:16, cursor:'pointer' }}>×</button>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:14 }}>
                  <span style={{ color:'var(--text2)' }}>{t('spent')} : <strong style={{ color }}>{fmt(b.spent)}</strong></span>
                  <span style={{ color:'var(--text2)' }}>{fmt(b.amount)}</span>
                </div>
                <div style={{ height:8, background:'var(--surface2)', borderRadius:4, overflow:'hidden', marginBottom:8 }}>
                  <div style={{ height:'100%', width:pct + '%', background:color, borderRadius:4, transition:'width 0.5s' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text2)' }}>
                  <span>{pct.toFixed(0)}%</span>
                  <span style={{ color: over ? 'var(--danger)' : 'var(--accent3)', fontWeight:600 }}>
                    {over ? t('exceeded') + ' ' + fmt(b.spent - b.amount) : t('remaining') + ' ' + fmt(b.amount - b.spent)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000090', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:20, padding:32, width:'100%', maxWidth:420 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h2 style={{ fontFamily:'Syne,sans-serif', fontWeight:700 }}>{t('add_budget')}</h2>
              <button onClick={() => setShowModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'var(--text2)', cursor:'pointer' }}>×</button>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('category')}</label>
              <select value={form.category_id} onChange={e => setForm({...form, category_id:e.target.value})} style={inp}>
                <option value="">-- Choisir --</option>
                {(cats || []).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:24 }}>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('amount')}</label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} style={inp} placeholder="0" />
            </div>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => setShowModal(false)} style={{ flex:1, padding:14, background:'var(--surface2)', color:'var(--text)', border:'1px solid var(--border)', borderRadius:10, cursor:'pointer' }}>{t('cancel')}</button>
              <button onClick={() => form.category_id && form.amount && saveMut.mutate(form)}
                style={{ flex:1, padding:14, background:'var(--accent)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, cursor:'pointer' }}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}